// apps/web/src/pages/coletor/apontamento-page.tsx — Fase 02, Plano 06 (APT-01, APT-04, APT-05, D-10, D-12).
import { useState, type ReactElement } from 'react';
import { RelogiosIndependentes } from '../../components/coletor/relogios-independentes';
import { ColetorBarraIdentidade } from '../../components/coletor/coletor-identidade';
import { FilaLocalBadge } from '../../components/coletor/fila-local';
import { apiMutate } from '../../lib/csrf-fetch';
import { lerContextoApontamento } from '../../lib/coletor-url';

export function ApontamentoPage(): ReactElement {
  const contexto = lerContextoApontamento(
    typeof window !== 'undefined' ? window.location.search : '',
  );

  const [qtdBoa, setQtdBoa] = useState<string>('');
  const [qtdNc, setQtdNc] = useState<string>('0');
  const [motivoNc, setMotivoNc] = useState<string>('');
  const [consumoPrevisto, setConsumoPrevisto] = useState<string>('');
  const [consumoReal, setConsumoReal] = useState<string>('');
  const [insumoLoteId, setInsumoLoteId] = useState<string>('');

  const [submitting, setSubmitting] = useState<boolean>(false);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const numQtdBoa = parseFloat(qtdBoa || '0');
  const numQtdNc = parseFloat(qtdNc || '0');
  const numConsumoReal = parseFloat(consumoReal || '0');
  const numConsumoPrev = parseFloat(consumoPrevisto || '0');

  const ncSemMotivo = numQtdNc > 0 && !motivoNc.trim();
  const formInvalido = !qtdBoa || numQtdBoa < 0 || numQtdNc < 0 || ncSemMotivo;

  const motivoDesabilitado = !qtdBoa
    ? 'Informe a quantidade boa para confirmar.'
    : ncSemMotivo
      ? 'Informe o motivo da não conformidade.'
      : null;

  const handleConfirmarApontamento = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!contexto || formInvalido) return;

    setSubmitting(true);
    setErrorMsg(null);
    setSuccessMsg(null);

    try {
      const res = await apiMutate('/api/v1/apontamentos/confirmar', {
        method: 'POST',
        body: {
          osId: contexto.osId,
          quantidadeBoa: numQtdBoa,
          quantidadeNaoConforme: numQtdNc,
          motivoNc: numQtdNc > 0 ? motivoNc.trim() : undefined,
          consumoPrevisto: consumoPrevisto.trim() === '' ? undefined : numConsumoPrev,
          consumoReal: consumoReal.trim() === '' ? undefined : numConsumoReal,
          insumoLoteId: insumoLoteId.trim() || undefined,
        },
      });

      if (!res.ok) {
        const errJson = (await res.json().catch(() => ({}))) as { message?: string };
        throw new Error(errJson.message || 'Falha ao confirmar apontamento.');
      }

      setSuccessMsg(
        `Apontamento confirmado. Boa: ${numQtdBoa} un · NC: ${numQtdNc} un${
          consumoReal.trim() === '' ? '' : ` · Consumo: ${numConsumoReal} kg`
        }`,
      );
      setQtdBoa('');
      setQtdNc('0');
      setMotivoNc('');
      setConsumoPrevisto('');
      setConsumoReal('');
      setInsumoLoteId('');
    } catch (err) {
      setErrorMsg(err instanceof Error ? err.message : 'Erro ao registrar apontamento');
    } finally {
      setSubmitting(false);
    }
  };

  const tituloOp = contexto?.opNumero ? `OP-${contexto.opNumero}` : 'OS identificada';
  const tituloEtapa = contexto?.etapaOrdem ? ` · Etapa ${contexto.etapaOrdem}` : '';

  return (
    <div
      className="coletor-apontamento-page min-h-screen bg-slate-950 text-slate-100 p-4 md:p-6 select-none font-sans"
      data-theme="dark-premium"
      data-density="touch"
    >
      <div className="max-w-4xl mx-auto space-y-6">
        <ColetorBarraIdentidade />

        <header className="bg-slate-900 border border-slate-800 rounded-2xl p-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 shadow-lg">
          <div>
            <div className="flex items-center gap-3">
              <span className="text-xs font-bold px-2.5 py-1 rounded bg-emerald-700/30 text-emerald-300 border border-emerald-500/40">
                Coletor
              </span>
              {contexto?.celulaId ? (
                <span className="text-xs font-mono text-slate-400 font-bold">{contexto.celulaId}</span>
              ) : null}
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold mt-1 text-white">
              {contexto ? (
                <>
                  {tituloOp}
                  <span className="text-slate-400 font-medium">{tituloEtapa}</span>
                </>
              ) : (
                'Registrar produção'
              )}
            </h1>
          </div>
          <FilaLocalBadge />
        </header>

        {!contexto ? (
          <main className="bg-slate-900 border border-slate-800 rounded-2xl p-6 shadow-xl space-y-4">
            <h2 className="text-lg font-bold text-white">Nenhuma OS identificada</h2>
            <p className="text-sm text-slate-300">
              Leia a etiqueta da ordem de serviço ou escolha a OS na fila da célula antes de apontar.
              Esta tela não assume uma ordem padrão.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 pt-2">
              <a
                href="/coletor/leitura"
                className="min-h-[56px] inline-flex items-center justify-center px-6 py-3 bg-emerald-700 hover:bg-emerald-600 text-white font-bold rounded-xl touch-manipulation"
              >
                Ler etiqueta
              </a>
              <a
                href="/coletor/fila"
                className="min-h-[56px] inline-flex items-center justify-center px-6 py-3 border border-slate-600 text-slate-100 font-bold rounded-xl touch-manipulation"
              >
                Abrir fila da célula
              </a>
            </div>
          </main>
        ) : (
          <>
            <RelogiosIndependentes osId={contexto.osId} />

            {successMsg && (
              <div className="p-4 bg-emerald-950/80 border border-emerald-500/60 text-emerald-200 rounded-xl text-sm font-bold shadow-md space-y-3">
                <p>{successMsg}</p>
                <div className="flex flex-col sm:flex-row gap-2">
                  <a
                    href="/coletor/fila"
                    className="min-h-[48px] inline-flex items-center justify-center px-4 bg-emerald-700 text-white rounded-xl text-sm font-bold touch-manipulation"
                  >
                    Próxima OS na fila
                  </a>
                  <a
                    href="/coletor/leitura"
                    className="min-h-[48px] inline-flex items-center justify-center px-4 border border-emerald-500/40 text-emerald-100 rounded-xl text-sm font-bold touch-manipulation"
                  >
                    Ler outra etiqueta
                  </a>
                </div>
              </div>
            )}

            {errorMsg && (
              <div className="p-4 bg-red-950/80 border border-red-500/60 text-red-200 rounded-xl text-sm font-bold shadow-md">
                {errorMsg}
              </div>
            )}

            <main className="bg-slate-900 border border-slate-800 rounded-2xl p-6 shadow-xl space-y-5">
              <div className="border-b border-slate-800 pb-3">
                <h2 className="text-lg font-bold text-white">Registrar produção</h2>
                <p className="text-xs text-slate-400 mt-0.5">
                  Quantidade boa, não conforme e consumo entram no mesmo evento. Não dá para editar depois —
                  só estornar.
                </p>
              </div>

              <form onSubmit={(e) => void handleConfirmarApontamento(e)} className="space-y-5">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-bold uppercase text-slate-300 mb-1.5" htmlFor="qtd-boa">
                      Quantidade boa *
                    </label>
                    <input
                      id="qtd-boa"
                      type="number"
                      required
                      min="0"
                      value={qtdBoa}
                      onChange={(e) => setQtdBoa(e.target.value)}
                      placeholder="0"
                      className="w-full min-h-[56px] px-4 py-3 bg-slate-950 border border-slate-750 text-white rounded-xl text-xl font-mono font-bold focus:ring-2 focus:ring-emerald-400 focus:outline-none"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-bold uppercase text-slate-300 mb-1.5" htmlFor="qtd-nc">
                      Quantidade não conforme
                    </label>
                    <input
                      id="qtd-nc"
                      type="number"
                      required
                      min="0"
                      value={qtdNc}
                      onChange={(e) => setQtdNc(e.target.value)}
                      placeholder="0"
                      className="w-full min-h-[56px] px-4 py-3 bg-slate-950 border border-slate-750 text-amber-400 rounded-xl text-xl font-mono font-bold focus:ring-2 focus:ring-amber-500 focus:outline-none"
                    />
                  </div>
                </div>

                {numQtdNc > 0 && (
                  <div className="p-4 bg-amber-950/30 border border-amber-500/40 rounded-xl space-y-2">
                    <label className="block text-xs font-bold uppercase text-amber-300" htmlFor="motivo-nc">
                      Motivo da não conformidade *
                    </label>
                    <input
                      id="motivo-nc"
                      type="text"
                      required
                      value={motivoNc}
                      onChange={(e) => setMotivoNc(e.target.value)}
                      placeholder="Ex.: espessura fora de tolerância, bolhas no banho…"
                      className="w-full min-h-[52px] px-4 py-2 bg-slate-950 border border-amber-600/60 text-white rounded-lg text-sm focus:ring-2 focus:ring-amber-500 focus:outline-none"
                    />
                  </div>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
                  <div>
                    <label className="block text-xs font-bold uppercase text-slate-300 mb-1.5" htmlFor="consumo-previsto">
                      Consumo previsto (kg / L)
                    </label>
                    <input
                      id="consumo-previsto"
                      type="number"
                      step="0.01"
                      min="0"
                      value={consumoPrevisto}
                      onChange={(e) => setConsumoPrevisto(e.target.value)}
                      className="w-full min-h-[48px] px-4 py-2 bg-slate-950 border border-slate-750 text-slate-300 rounded-xl text-base font-mono focus:ring-2 focus:ring-emerald-400 focus:outline-none"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-bold uppercase text-slate-300 mb-1.5" htmlFor="consumo-real">
                      Consumo real (kg / L)
                    </label>
                    <input
                      id="consumo-real"
                      type="number"
                      step="0.01"
                      min="0"
                      value={consumoReal}
                      onChange={(e) => setConsumoReal(e.target.value)}
                      className="w-full min-h-[48px] px-4 py-2 bg-slate-950 border border-slate-750 text-emerald-400 rounded-xl text-base font-mono font-bold focus:ring-2 focus:ring-emerald-500 focus:outline-none"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-bold uppercase text-slate-400 mb-1" htmlFor="lote-insumo">
                    Lote de insumo (opcional)
                  </label>
                  <input
                    id="lote-insumo"
                    type="text"
                    value={insumoLoteId}
                    onChange={(e) => setInsumoLoteId(e.target.value)}
                    placeholder="Código do lote do banho ou tinta, se houver"
                    className="w-full min-h-[44px] px-3 py-2 bg-slate-950 border border-slate-800 text-slate-400 rounded-lg text-xs font-mono focus:ring-2 focus:ring-emerald-400 focus:outline-none"
                  />
                </div>

                <div className="pt-4 border-t border-slate-800 space-y-2">
                  {motivoDesabilitado && (
                    <p className="text-xs text-amber-300" role="status">
                      {motivoDesabilitado}
                    </p>
                  )}
                  <button
                    type="submit"
                    disabled={submitting || formInvalido}
                    className="w-full min-h-[64px] py-4 bg-emerald-700 hover:bg-emerald-600 active:bg-emerald-800 disabled:opacity-30 text-white font-bold text-lg rounded-xl shadow-xl transition uppercase tracking-wider touch-manipulation focus:ring-4 focus:ring-emerald-400/50"
                  >
                    {submitting ? 'Gravando…' : 'Confirmar apontamento'}
                  </button>
                </div>
              </form>
            </main>
          </>
        )}
      </div>
    </div>
  );
}
